/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';

import { GlobalCatch } from '../../core/error/GlobalCatch';
import { Storage } from '../../core/storage/Storage';

interface GlobalCatchProps {
    name: string;
    onCatch: Function;
    retryLimit?: number | undefined;
}

const useGlobalCatch = ({ name, onCatch, retryLimit }: GlobalCatchProps) => {
    // One-time GlobalCatch instance per hook instance
    const globalCatchRef = useRef<GlobalCatch>();
    if (!globalCatchRef.current) {
        globalCatchRef.current = GlobalCatch.getInstance(name);
    }
    const globalCatch = globalCatchRef.current;
    const sessionRefKey = `_${name}_RETRY_COUNT`;
    const catchCount =
        Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItem(sessionRefKey) || 0;

    const actualRetryLimit = retryLimit && retryLimit > 0 ? retryLimit : 1;
    const isRetryable = catchCount < actualRetryLimit;

    const processError = (error) => {
        if (!isRetryable) return;

        globalCatch.parse(error);
        globalCatch.save();

        if (actualRetryLimit > 1) {
            Storage.getInstance(Storage.StorageType.SESSION_STORAGE).setItem(
                sessionRefKey,
                catchCount + 1,
            );
        }

        onCatch();
    };

    const clearRetries = () => {
        Storage.getInstance(Storage.StorageType.SESSION_STORAGE).removeItem(sessionRefKey);
    };

    const isRetriesDone = (): boolean => {
        return !isRetryable;
    };

    useEffect(() => {
        window.addEventListener('error', (ev: ErrorEvent) => {
            ev.preventDefault();
            processError(ev);
        });

        let clearRetriesTimeoutRef: NodeJS.Timeout | undefined;
        if (!isRetryable) {
            clearRetries();
        } else {
            // Set a timeout to clear retries after 5 minutes after the app starts
            // This is to app exit operation when the app crash is not frequent.
            clearRetriesTimeoutRef = setTimeout(clearRetries, 5 * 60 * 1000);
        }

        return () => {
            window.removeEventListener('error', () => true);
            clearTimeout(clearRetriesTimeoutRef);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        current: globalCatch.getLatestCatch(),
        catches: globalCatch.getCatches,
        clear: globalCatch.clear,
        isRetriesDone,
    };
};

useGlobalCatch.propTypes = {
    name: PropTypes.string.isRequired,
    onCatch: PropTypes.func.isRequired,
    retryLimit: PropTypes.number,
};

export default useGlobalCatch;
